import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { observer } from 'mobx-react-lite'
import { useStore } from '../../../contexts/store'
import { Box, Paper, Slider, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material'

import { PodClass } from '../../../classes/Pod'
import { interactionTypeLiterals } from '../../../shared/src/types/Interaction'
import dayjs from 'dayjs'
import Debounce from '../../Helper/ReactDebounceComponent/ReactDebounceComponent'
import I18nMd from '../../PrettyMarkdownInput/I18nMd'

const t0 = dayjs('2020-01-01 12:00:00')

const calculate = (pod:PodClass) => {
  var earliest:number = dayjs().unix()
  var latest:number = 0
  var interactionsPerDay:{[day:string]:number} = {}

  for (const [_, iLoc] of Object.entries(pod.interactionsLookupTable)) {
    const interaction = pod.getInteraction(iLoc)
    if (!interaction) continue
    earliest = Math.min(earliest, interaction.tCreated)
    latest = Math.max(latest, interaction.tCreated)
    const day = dayjs.unix(interaction.tCreated).diff(t0, 'd')
    interactionsPerDay[`${day}`] = (interactionsPerDay[`${day}`] || 0) + 1
  }

  for (const [_, thread] of Object.entries(pod.content.threads)) {
    for(const msg of thread.messages) {
    if (!msg || !msg.tCreated) continue
    earliest = Math.min(earliest, msg.tCreated)
    latest = Math.max(latest, msg.tCreated)
    const day = dayjs.unix(msg.tCreated).diff(t0, 'd')
    interactionsPerDay[`${day}`] = (interactionsPerDay[`${day}`] || 0) + 1
    }
  }


  return {
    earliest,
    latest,
    interactionsPerDay,
  }
}

const PodStatistics = () => {
  const { t } = useTranslation()
  const { podStore, uiStore, sessionStore } = useStore()
  const pod:PodClass|null = podStore.pod
  const session = sessionStore.session
  const data = useMemo(() => { if (pod) return calculate(pod); return null }, [pod])

  if (!pod || !session || !data) return null

  const [timeframe, setTimeframe] = uiStore.useUiState<number[]>(`${pod.podId}-podStatisticsTimeframe`, [0,100])
  const { earliest, latest, interactionsPerDay } = data

  if (!latest || !earliest) return <div>{t('No interactions found')}</div>

  const timeScale = (latest-earliest)/100

  const sliderToDate = (s:number) => { return dayjs.unix(s*timeScale + earliest).format('YYYY-MM-DD HH:mm') }

  const from = dayjs.unix(timeframe[0]*timeScale + earliest)
  const to = dayjs.unix(timeframe[1]*timeScale + earliest)
  const fromDay = from.diff(t0, 'd')
  const toDay   = to.diff(t0, 'd')
  const totalDays = dayjs.unix(latest).diff(dayjs.unix(earliest), 'd')
  const nofDays = to.diff(from, 'day')
  const t0diff = dayjs.unix(earliest).diff(t0, 'd')

  const barGraphBar:React.JSX.Element[] = []
  const height = 40
  const maxInteractionPerDay = Math.max(...Object.values(interactionsPerDay))

  for(var i=t0diff; i<t0diff+totalDays; i++) {
    barGraphBar.push(<div key={i} style={{
      overflow:'hidden',
      backgroundColor:'#eee',
      height,
      borderBottom:`${((height * interactionsPerDay[i] / maxInteractionPerDay)||0)}px solid #00a`,
      opacity: (i>fromDay && i<=toDay) ? 1 : .5,
    }} title={String(t(`{{nof}} Interactions on {{day}}`, { nof:interactionsPerDay[i]||0, day: t0.add(i, 'd').format('YYYY-MM-DD')  }))}></div>)
  }
  const activityBarGraph = <div style={{display: 'grid', gridTemplateColumns: `repeat(${barGraphBar.length}, 1fr)`, gridTemplateRows: '1fr', gridColumnGap: 0, gridRowGap: 0, borderBottom:'1px solid #000' }}>{barGraphBar}</div>

  return <div style={{position:'relative'}}>
    {t('Activity per User:')}
    <Slider
        getAriaLabel={() => t('Set the Time Range')}
        value={timeframe}
        onChange={(_:Event, newValues: number|number[]) => { if (typeof newValues === 'object') setTimeframe(newValues) }}
        valueLabelDisplay="auto"
        step={.1}
        getAriaValueText={sliderToDate}
        valueLabelFormat={sliderToDate}
      />
    <I18nMd value="Show **{{nofDays}}** days between **{{from}}** to **{{to}}**" variables={{nofDays:nofDays, from: from.format('YYYY-MM-DD'), to: to.format('YYYY-MM-DD')}} />
    {activityBarGraph}
    <div>

    </div>
    <Debounce ms={300}>
      <InteractionsPerUser from={from.unix()} to={to.unix()} />
      <ActivityScore from={from.unix()} to={to.unix()} />
    </Debounce>
    </div>
}

export default observer(PodStatistics)

type DataCollection = {[userId: number]:{ [interactionType:string]:number }}


const calculateIpU = (pod:PodClass, from:number,to:number) => {
  var nofInteractions = 0
  var nofMessages = 0
  var collection:DataCollection = {}
  for (const [_, iLoc] of Object.entries(pod.interactionsLookupTable)) {
    const interaction = pod.getInteraction(iLoc)
    if (!interaction) continue
    if (interaction.tCreated<from) continue
    if (interaction.tCreated>to) continue
    if (typeof collection[interaction.userId] === 'undefined') collection[interaction.userId] = {}
    if (typeof collection[interaction.userId][interaction.interactionType] === 'undefined') collection[interaction.userId][interaction.interactionType] = 0
    collection[interaction.userId][interaction.interactionType]++
    collection[interaction.userId]['total'] = (collection[interaction.userId]['total']||0)+1
    if (interaction.interactionType !== 'annotation') collection[interaction.userId]['publics'] = (collection[interaction.userId]['publics']||0)+1
    nofInteractions++
  }
  for (const [_, thread] of Object.entries(pod.content.threads)) {
    for(const msg of thread.messages) {
      if (!msg || !msg.tCreated) continue
      if (msg.tCreated<from) continue
      if (msg.tCreated>to) continue
      if (typeof collection[msg.userId] === 'undefined') collection[msg.userId] = {}
      if (typeof collection[msg.userId]['message'] === 'undefined') collection[msg.userId]['message'] = 0
      collection[msg.userId]['message']++
      collection[msg.userId]['total'] = (collection[msg.userId]['total']||0)+1
      collection[msg.userId]['publics'] = (collection[msg.userId]['publics']||0)+1
      nofMessages++
    }
  }
  return { t0:from, t1:to, nofInteractions, nofMessages, collection }
}


const hashtable2array = (hash:{[key:string]:any}, sorting:((a:any, b:any)=>number)|null=null) => {
  const array = Object.keys(hash).map((key:string) => ({ ...hash[key], _key:key }))
  if (sorting) array.sort(sorting)
  return array
}

const InteractionsPerUser = ({from, to}: {from: number, to: number}) => {

  const { podStore } = useStore()
  const { t } = useTranslation()
  const pod:PodClass|null = podStore.pod

  const data = useMemo(()=>{ if (pod) return calculateIpU(pod, from, to); return null }, [pod, from, to])

  if (!pod || !data) return null

  const { nofInteractions, nofMessages, collection } = data

  const interactionTypes = [...interactionTypeLiterals]

  return <div style={{marginTop:40, }}>
    {t('In total, there are {{nofInteractions}} interactions and {{nofMessages}} messages:', {nofInteractions, nofMessages})}
    <TableContainer component={Paper} sx={[{mt:2, width:'100%', overflow:'scroll'} ,{'td': { overflow:'hidden', whiteSpace:'normal' }, 'th': {overflow:'hidden'}}]}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>{t('Name')}</TableCell>
            {interactionTypes.map(i => <TableCell align='center' key={i}>{t(i)}</TableCell>)}
          </TableRow>
        </TableHead>
        <TableBody>
          {Object.keys(collection).map((userId:string) => {
            return <TableRow key={userId} sx={[{'&:hover': { backgroundColor: '#eee' }}]}>
              <TableCell>{pod.userInfos[Number(userId)]?.userName || <i>unknown</i>}</TableCell>
              { interactionTypes.map(i => <TableCell align='center' key={i}>{collection[Number(userId)][i]}</TableCell>) }
            </TableRow>
          })}
        </TableBody>
      </Table>
    </TableContainer>
  </div>
}



const ActivityScore = ({from, to}: {from: number, to: number}) => {

  const { podStore, sessionStore } = useStore()
  const { t } = useTranslation()
  const pod:PodClass|null = podStore.pod
  const session = sessionStore.session

  const data = useMemo(()=>{
    if (pod) {
      return hashtable2array(calculateIpU(pod, from, to).collection, (a, b) => { return b.publics - a.publics })
    }
    return null
  }, [pod, from, to])

  if (!pod || !data || !session) return null

  const maxTotal = Math.max(...data.map(r => r.publics))
  const height = 200
  var selfPos = 0

  const r:React.JSX.Element[] = data.map((d, i) => {
    if (Number(d._key) === session.user.userId) selfPos = i+1
    return <div
    key={i}
    style={{
      overflow:'hidden',
      backgroundColor:'#eee',
      height,
      borderBottom:`${((height * d.publics / maxTotal)||0)}px solid ${(Number(d._key) === session.user.userId) ? '#a00' : '#666'}`,
    }}
    title={ (Number(d._key) === session.user.userId) ? 'You have ' + d.publics + ' interactions.' : d.publics + ' Interactions' }
    ></div>
  })

  const ipD = <div style={{display: 'grid', gridTemplateColumns: `repeat(${r.length}, 1fr)`, gridTemplateRows: '1fr', gridColumnGap: 0, gridRowGap: 0, borderBottom:'1px solid #000' }}>{r}</div>

  return <div style={{marginTop:20, }}>
    <Box sx={{pt:4}}>{t('You are on Position {{selfPos}} in total interactions:', {selfPos})}</Box>
    {ipD}
  </div>
}